import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from "@chakra-ui/react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import {
	FiChevronDown,
	FiChevronUp,
	FiMenu,
	FiPlus,
	FiX,
} from "react-icons/fi";

import { Button } from "newui/components/Button";
import { Modal as AlertModal } from "newui/components/ui/Modal";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel";
import { InfoTooltip } from "newui/components/InfoTooltip/InfoTooltip";
import { cn } from "utils/helpers";
import type { QuoteColumnType } from "./quote-table.types";
import type { QuoteTemplate } from "../../api/quote-templates-api";
import type { UnitOption } from "./DynamicQuoteTable";

export interface ColumnConfig {
	id?: number;
	columnType: QuoteColumnType;
	label: string;
	sortOrder: number;
	localId: string;
}

export interface DefaultRowConfig {
	name: string;
	unit_id: number | null;
	sort_order: number;
}

export interface QuoteTableConfig {
	name: string;
	description: string;
	columns: ColumnConfig[];
	presetType?: string;
	defaultRows?: DefaultRowConfig[];
}

export interface CreateQuoteTableModalProps {
	existingColumns?: ColumnConfig[];
	existingDescription?: string;
	existingTableName?: string;
	isOpen: boolean;
	isSubmitting?: boolean;
	onClose: () => void;
	onSubmit: (config: QuoteTableConfig) => void;
	templates?: QuoteTemplate[];
	unitOptions?: UnitOption[];
}

interface ColumnTypeOption {
	value: QuoteColumnType;
	label: string;
	maxPerTable: number | null;
}

const COLUMN_TYPE_OPTIONS: ColumnTypeOption[] = [
	{ value: "description", label: "Description", maxPerTable: null },
	{ value: "unit_of_measurement", label: "Units", maxPerTable: 1 },
	{ value: "response_quantity", label: "Response: Quantity", maxPerTable: 1 },
	{ value: "response_value", label: "Response: Value", maxPerTable: null },
	{ value: "response_number", label: "Response: Number", maxPerTable: null },
	{ value: "response_text", label: "Response: Text", maxPerTable: null },
	{ value: "total", label: "Total", maxPerTable: 1 },
];

const SAMPLE_CELL_LABELS: Record<QuoteColumnType, string> = {
	description: "{description}",
	unit_of_measurement: "{uom}",
	response_quantity: "{qty}",
	response_value: "{value}",
	response_number: "{number}",
	response_text: "{text}",
	total: "{calculated}",
};

interface PresetDefinition {
	columns: Omit<ColumnConfig, "localId">[];
	rows: DefaultRowConfig[];
}

const DEFAULT_COLUMNS: Omit<ColumnConfig, "localId">[] = [
	{ columnType: "description", label: "Description of Scope", sortOrder: 1 },
	{ columnType: "unit_of_measurement", label: "Unit of Measurement", sortOrder: 2 },
	{ columnType: "response_quantity", label: "Quantity", sortOrder: 3 },
	{ columnType: "response_value", label: "Rate", sortOrder: 4 },
	{ columnType: "total", label: "Cost", sortOrder: 5 },
];

const PRESETS: Record<string, PresetDefinition> = {
	preliminaries: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "E.g. Concept Design", unit_id: 7, sort_order: 1 },
			{ name: "E.g. Design Development", unit_id: 7, sort_order: 2 },
			{ name: "E.g. Contract Documentation", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Meeting Attendance", unit_id: 7, sort_order: 4 },
		],
	},
	trade_package: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "E.g. Concrete Supply", unit_id: 4, sort_order: 1 },
			{ name: "E.g. Concrete Install", unit_id: 4, sort_order: 2 },
			{ name: "E.g. Contract Repairs", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Supervisor", unit_id: 8, sort_order: 4 },
		],
	},
	contingency: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "E.g. Contingency 1", unit_id: 3, sort_order: 1 },
			{ name: "E.g. Contingency 2", unit_id: 3, sort_order: 2 },
		],
	},
	professional_fees: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "E.g. Concept Design", unit_id: 7, sort_order: 1 },
			{ name: "E.g. Design Development", unit_id: 7, sort_order: 2 },
			{ name: "E.g. Contract Documentation", unit_id: 7, sort_order: 3 },
			{ name: "E.g. Meeting Attendance", unit_id: 7, sort_order: 4 },
		],
	},
	provisional_sums: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "E.g. Landscaping (Supply & Install)", unit_id: 3, sort_order: 1 },
			{ name: "E.g. Irrigation (Supply & Install)", unit_id: 3, sort_order: 2 },
		],
	},
	other: {
		columns: DEFAULT_COLUMNS,
		rows: [
			{ name: "", unit_id: null, sort_order: 1 },
			{ name: "", unit_id: null, sort_order: 2 },
			{ name: "", unit_id: null, sort_order: 3 },
			{ name: "", unit_id: null, sort_order: 4 },
		],
	},
};

const BLANK_ROWS: DefaultRowConfig[] = [
	{ name: "", unit_id: null, sort_order: 1 },
	{ name: "", unit_id: null, sort_order: 2 },
	{ name: "", unit_id: null, sort_order: 3 },
	{ name: "", unit_id: null, sort_order: 4 },
];

const PRESET_OPTIONS = [
	{ value: "", label: "Custom" },
	{ value: "preliminaries", label: "Preliminaries" },
	{ value: "trade_package", label: "Trade: Materials, Labor, Plant & Equip." },
	{ value: "contingency", label: "Contingency" },
	{ value: "professional_fees", label: "Professional Fees" },
	{ value: "provisional_sums", label: "Provisional SUM/S" },
	{ value: "other", label: "Other" },
];

const COLUMN_TYPE_DESCRIPTIONS: Record<QuoteColumnType, string> = {
	description: "A text field for describing the scope item or line item name.",
	unit_of_measurement: "Dropdown for the unit of measure (e.g. m², hours, each). Only one per table.",
	response_quantity: "Numeric field where bidders enter the quantity. Only one per table.",
	response_value: "Numeric field where bidders enter a dollar value or rate.",
	response_number: "Generic numeric field for any number entry.",
	response_text: "Free-text field where bidders can type a response.",
	total: "Auto-calculated column that sums all Response: Value columns. Requires at least one Value column. Only one per table.",
};

const cellBorder = "border border-[#EAECF0]";

const CreateQuoteTableModal: React.FC<CreateQuoteTableModalProps> = ({
	existingColumns,
	existingDescription,
	existingTableName,
	isOpen,
	isSubmitting = false,
	onClose,
	onSubmit,
	templates = [],
	unitOptions = [],
}) => {
	const localIdCounterRef = useRef(0);
	const generateLocalId = (): string => {
		localIdCounterRef.current += 1;
		return `col-local-${localIdCounterRef.current}-${Date.now()}`;
	};
	const addLocalIds = (cols: Omit<ColumnConfig, "localId">[]): ColumnConfig[] => {
		return cols.map((c) => ({ ...c, localId: generateLocalId() }));
	};

	const isEditMode = !!existingTableName;

	const [tableName, setTableName] = useState("");
	const [tableDescription, setTableDescription] = useState("");
	const [columns, setColumns] = useState<ColumnConfig[]>([]);
	const [selectedPreset, setSelectedPreset] = useState("");
	const [hasUserModified, setHasUserModified] = useState(false);
	const [defaultRows, setDefaultRows] = useState<DefaultRowConfig[]>(BLANK_ROWS);

	const [showPresetWarning, setShowPresetWarning] = useState(false);
	const [pendingPreset, setPendingPreset] = useState("");
	const [showTotalRemovalWarning, setShowTotalRemovalWarning] = useState(false);
	const [pendingColumnDelete, setPendingColumnDelete] = useState<string | null>(null);

	const newColumnInputRef = useRef<HTMLInputElement | null>(null);

	const presetSelectOptions: SelectOption[] = useMemo(() => {
		const templateOptions: SelectOption[] = templates.map((tmpl) => ({
			label: `My Template: ${tmpl.name}`,
			value: `template_${tmpl.id}`,
		}));
		const builtInOptions: SelectOption[] = PRESET_OPTIONS.map((opt) => ({
			label: opt.label,
			value: opt.value,
		}));
		return [...templateOptions, ...builtInOptions];
	}, [templates]);

	const selectedPresetOption = useMemo(() => {
		return presetSelectOptions.find((o) => o.value === selectedPreset) || presetSelectOptions[0];
	}, [presetSelectOptions, selectedPreset]);

	// Reset state when modal opens
	useEffect(() => {
		if (isOpen) {
			if (isEditMode) {
				const cols = existingColumns && existingColumns.length > 0
					? existingColumns.map((c) => ({
						...c,
						localId: c.localId || generateLocalId(),
					}))
					: addLocalIds(DEFAULT_COLUMNS);
				setColumns(cols);
				setTableName(existingTableName || "");
				setTableDescription(existingDescription || "");
				setSelectedPreset("");
				setHasUserModified(false);
			} else {
				setColumns(addLocalIds(DEFAULT_COLUMNS));
				setTableName("");
				setTableDescription("");
				setSelectedPreset("");
				setHasUserModified(false);
				setDefaultRows(BLANK_ROWS);
			}
		}
	}, [isOpen, isEditMode, existingColumns, existingTableName, existingDescription]);

	// Column type counts for constraint enforcement
	const typeCounts = useMemo(() => {
		const counts: Partial<Record<QuoteColumnType, number>> = {};
		columns.forEach((col) => {
			counts[col.columnType] = (counts[col.columnType] || 0) + 1;
		});
		return counts;
	}, [columns]);

	const hasValue = (typeCounts.response_value || 0) > 0;

	const isTypeDisabled = useCallback(
		(type: QuoteColumnType, currentColType?: QuoteColumnType): boolean => {
			const option = COLUMN_TYPE_OPTIONS.find((o) => o.value === type);
			if (!option) {
				return false;
			}
			if (type === "total") {
				if (!hasValue) {
					return true;
				}
			}
			if (option.maxPerTable === null) {
				return false;
			}
			const count = typeCounts[type] || 0;
			if (currentColType === type) {
				return false;
			}
			return count >= option.maxPerTable;
		},
		[typeCounts, hasValue]
	);

	const getDisabledReason = useCallback(
		(type: QuoteColumnType): string => {
			if (type === "total" && !hasValue) {
				return "Total requires at least one Response: Value column";
			}
			if (type === "unit_of_measurement") {
				return "Only 1 Units column allowed per table";
			}
			if (type === "response_quantity") {
				return "Only 1 Quantity column allowed per table";
			}
			if (type === "total") {
				return "Only 1 Total column allowed per table";
			}
			return "";
		},
		[hasValue]
	);

	const markModified = useCallback(() => {
		setHasUserModified(true);
		setSelectedPreset("");
	}, []);

	const handleAddColumn = useCallback(() => {
		const newCol: ColumnConfig = {
			columnType: "response_text",
			label: "New Column",
			sortOrder: columns.length + 1,
			localId: generateLocalId(),
		};
		setColumns([...columns, newCol]);
		markModified();
		// Focus the new column's input on next render
		setTimeout(() => {
			if (newColumnInputRef.current) {
				newColumnInputRef.current.focus();
				newColumnInputRef.current.select();
			}
		}, 50);
	}, [columns, markModified]);

	const wouldRemoveTotalColumn = useCallback(
		(localId: string): boolean => {
			const col = columns.find((c) => c.localId === localId);
			if (!col) {
				return false;
			}
			const hasTotal = columns.some((c) => c.columnType === "total");
			if (!hasTotal) {
				return false;
			}
			if (col.columnType === "response_value") {
				const valueCount = columns.filter((c) => c.columnType === "response_value").length;
				return valueCount <= 1;
			}
			return false;
		},
		[columns]
	);

	const executeColumnDelete = useCallback(
		(localId: string) => {
			const col = columns.find((c) => c.localId === localId);
			if (!col) {
				return;
			}
			let newColumns = columns.filter((c) => c.localId !== localId);
			// Auto-remove total if all response_value columns are gone
			if (col.columnType === "response_value") {
				const remainingVal = newColumns.some((c) => c.columnType === "response_value");
				if (!remainingVal) {
					newColumns = newColumns.filter((c) => c.columnType !== "total");
				}
			}
			// Recalculate sort orders
			newColumns = newColumns.map((c, idx) => ({ ...c, sortOrder: idx + 1 }));
			setColumns(newColumns);
			markModified();
		},
		[columns, markModified]
	);

	const handleRemoveColumn = useCallback(
		(localId: string) => {
			if (columns.length <= 1) {
				return;
			}
			if (wouldRemoveTotalColumn(localId)) {
				setPendingColumnDelete(localId);
				setShowTotalRemovalWarning(true);
				return;
			}
			executeColumnDelete(localId);
		},
		[columns.length, wouldRemoveTotalColumn, executeColumnDelete]
	);

	const handleColumnTypeChange = useCallback(
		(localId: string, newType: QuoteColumnType) => {
			setColumns(
				columns.map((c) =>
					c.localId === localId ? { ...c, columnType: newType } : c
				)
			);
			markModified();
		},
		[columns, markModified]
	);

	const handleColumnLabelChange = useCallback(
		(localId: string, newLabel: string) => {
			setColumns(
				columns.map((c) =>
					c.localId === localId ? { ...c, label: newLabel } : c
				)
			);
			markModified();
		},
		[columns, markModified]
	);

	const handleMoveColumn = useCallback(
		(localId: string, direction: "up" | "down") => {
			const idx = columns.findIndex((c) => c.localId === localId);
			if (idx === -1) {
				return;
			}
			const newIdx = direction === "up" ? idx - 1 : idx + 1;
			if (newIdx < 0 || newIdx >= columns.length) {
				return;
			}
			const reordered = [...columns];
			const [moved] = reordered.splice(idx, 1);
			reordered.splice(newIdx, 0, moved);
			setColumns(reordered.map((c, i) => ({ ...c, sortOrder: i + 1 })));
			markModified();
		},
		[columns, markModified]
	);

	const handleDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination) {
				return;
			}
			const startIndex = result.source.index;
			const endIndex = result.destination.index;
			if (startIndex === endIndex) {
				return;
			}
			const reordered = [...columns];
			const [moved] = reordered.splice(startIndex, 1);
			reordered.splice(endIndex, 0, moved);
			setColumns(reordered.map((c, i) => ({ ...c, sortOrder: i + 1 })));
			markModified();
		},
		[columns, markModified]
	);

	const applyPreset = useCallback(
		(presetKey: string) => {
			if (presetKey === "") {
				setColumns(addLocalIds(DEFAULT_COLUMNS));
				setDefaultRows(BLANK_ROWS);
				setSelectedPreset("");
				setHasUserModified(false);
				return;
			}
			// Check if it's a template
			if (presetKey.startsWith("template_")) {
				const templateId = Number(presetKey.replace("template_", ""));
				const template = templates.find((t) => t.id === templateId);
				if (template) {
					const templateCols: Omit<ColumnConfig, "localId">[] = template.columns.map((c) => ({
						columnType: c.column_type as QuoteColumnType,
						label: c.label,
						sortOrder: c.sort_order,
					}));
					const templateRows: DefaultRowConfig[] = template.rows.map((r) => ({
						name: r.name,
						unit_id: r.unit_id,
						sort_order: r.sort_order,
					}));
					setColumns(addLocalIds(templateCols.length > 0 ? templateCols : DEFAULT_COLUMNS));
					setDefaultRows(templateRows.length > 0 ? templateRows : BLANK_ROWS);
				}
			} else {
				const preset = PRESETS[presetKey];
				if (preset) {
					setColumns(addLocalIds(preset.columns));
					setDefaultRows(preset.rows);
				}
			}
			setSelectedPreset(presetKey);
			setHasUserModified(false);
		},
		[templates]
	);

	const handlePresetChange = useCallback(
		(newPreset: string) => {
			if (hasUserModified) {
				setPendingPreset(newPreset);
				setShowPresetWarning(true);
			} else {
				applyPreset(newPreset);
			}
		},
		[hasUserModified, applyPreset]
	);

	const handleSubmit = useCallback(() => {
		const config: QuoteTableConfig = {
			name: tableName.trim() || "Quote Table",
			description: tableDescription.trim(),
			columns: columns,
			presetType: selectedPreset || "custom",
			defaultRows: defaultRows,
		};
		onSubmit(config);
	}, [tableName, tableDescription, columns, selectedPreset, defaultRows, onSubmit]);

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size="full"
				scrollBehavior="inside"
			>
				<ModalOverlay />
				<ModalContent data-testid="create-quote-table-modal">
					<ModalHeader>
						{isEditMode ? "Edit Table Layout" : "Create Quote Table Structure"}
					</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<div className="flex gap-6">
							{/* Left side: Form fields */}
							<div className="w-1/2">
									{/* Preset Selector */}
								{!isEditMode && (
									<div className="mb-6">
										<SelectWithLabel
											label="Preset"
											onChange={(option) => {
												const val = (option as SelectOption | null)?.value ?? "";
												handlePresetChange(val);
											}}
											options={presetSelectOptions}
											testId="preset-selector"
											value={selectedPresetOption}
										/>
									</div>
								)}

								{/* Quote Table Columns Section */}
								<div className="mb-4">
									<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
										Quote Table Columns
									</p>

									{/* Column headers */}
									<div className="mb-1 flex items-center gap-2 px-2">
										<div className="w-[14px]" />
										<p className="flex-1 text-xs font-medium text-gray-500">
											Header Text
										</p>
										<div className="flex w-[200px] items-center gap-1">
											<p className="text-xs font-medium text-gray-500">
												Column Type
											</p>
											<InfoTooltip
												size={12}
												tooltipText={COLUMN_TYPE_OPTIONS.map(
													(opt) => `${opt.label}: ${COLUMN_TYPE_DESCRIPTIONS[opt.value]}`
												).join("\n")}
											/>
										</div>
										<div className="w-[24px]" />
										<div className="w-[14px]" />
									</div>

									<DragDropContext onDragEnd={handleDragEnd}>
										<Droppable droppableId="column-editor" direction="vertical">
											{(provided) => (
												<div
													ref={provided.innerRef}
													{...provided.droppableProps}
													className="space-y-1"
													data-testid="column-editor"
												>
													{columns.map((col, idx) => (
														<Draggable
															key={col.localId}
															draggableId={col.localId}
															index={idx}
														>
															{(dragProvided) => (
																<div
																	ref={dragProvided.innerRef}
																	{...dragProvided.draggableProps}
																	className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
																	data-testid={`column-row-${idx}`}
																>
																	<div
																		{...dragProvided.dragHandleProps}
																		className="cursor-grab text-gray-400"
																	>
																		<FiMenu size={14} />
																	</div>

																	<input
																		aria-label={`Column ${idx + 1} name`}
																		className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
																		data-testid={`column-name-${idx}`}
																		onChange={(e) =>
																			handleColumnLabelChange(col.localId, e.target.value)
																		}
																		ref={idx === columns.length - 1 ? newColumnInputRef : undefined}
																		value={col.label}
																	/>

																	<select
																		aria-label={`Column ${idx + 1} type`}
																		className="w-[200px] rounded border border-gray-300 bg-white px-2 py-1 text-sm"
																		data-testid={`column-type-${idx}`}
																		onChange={(e) =>
																			handleColumnTypeChange(
																				col.localId,
																				e.target.value as QuoteColumnType
																			)
																		}
																		value={col.columnType}
																	>
																		{COLUMN_TYPE_OPTIONS.map((opt) => {
																			const disabled = isTypeDisabled(opt.value, col.columnType);
																			return (
																				<option
																					key={opt.value}
																					disabled={disabled}
																					title={disabled ? getDisabledReason(opt.value) : undefined}
																					value={opt.value}
																				>
																					{opt.label}
																				</option>
																			);
																		})}
																	</select>

																	<div className="flex flex-col">
																		<button
																			aria-label={`Move column ${idx + 1} up`}
																			className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
																			disabled={idx === 0}
																			onClick={() => handleMoveColumn(col.localId, "up")}
																			type="button"
																		>
																			<FiChevronUp size={12} />
																		</button>
																		<button
																			aria-label={`Move column ${idx + 1} down`}
																			className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
																			disabled={idx === columns.length - 1}
																			onClick={() => handleMoveColumn(col.localId, "down")}
																			type="button"
																		>
																			<FiChevronDown size={12} />
																		</button>
																	</div>

																	<button
																		aria-label={`Remove column ${col.label}`}
																		className={cn(
																			"text-gray-400 hover:text-red-500",
																			columns.length <= 1 && "cursor-not-allowed opacity-30"
																		)}
																		data-testid={`remove-column-${idx}`}
																		disabled={columns.length <= 1}
																		onClick={() => handleRemoveColumn(col.localId)}
																		type="button"
																	>
																		<FiX size={14} />
																	</button>
																</div>
															)}
														</Draggable>
													))}
													{provided.placeholder}
												</div>
											)}
										</Droppable>
									</DragDropContext>

									<div className="mt-2">
										<Button
											btntype="plain-border"
											data-testid="add-column-btn"
											onClick={handleAddColumn}
											size="sm"
											type="button"
										>
											<FiPlus className="mr-1" />
											Add Column
										</Button>
									</div>
								</div>
							</div>

							{/* Right side: Preview */}
							<div className="w-1/2">
								<p className="mb-2 font-roboto text-sm font-semibold text-gray-900">
									Preview
								</p>
								<div className="overflow-x-auto rounded-lg border border-[#EAECF0]">
									<table
										className="w-full table-auto"
										data-testid="sample-row-preview"
									>
										<caption className="sr-only">
											Preview of table with current column configuration
										</caption>
										<thead>
											<tr className="bg-[#F9FAFB]">
												<th
													className={cn(
														cellBorder,
														"w-10 px-2 py-2 text-center font-roboto text-xs font-medium text-tertiary-600"
													)}
												>
													#
												</th>
												{columns.map((col) => (
													<th
														key={col.localId}
														className={cn(
															cellBorder,
															"px-2 py-2 font-roboto text-xs font-medium text-tertiary-600"
														)}
													>
														{col.label}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{defaultRows.length > 0 ? (
												defaultRows.map((row, rowIdx) => (
													<tr key={row.sort_order}>
														<td
															className={cn(
																cellBorder,
																"bg-gray-50 text-center font-roboto text-xs text-primary-500"
															)}
														>
															{rowIdx + 1}
														</td>
														{columns.map((col) => (
															<td
																key={col.localId}
																className={cn(
																	cellBorder,
																	"px-2 py-2 font-roboto text-xs",
																	row.name
																		? "text-gray-600"
																		: "italic text-gray-400"
																)}
															>
																{col.columnType === "description"
																	? row.name || SAMPLE_CELL_LABELS[col.columnType]
																	: col.columnType === "unit_of_measurement" && row.unit_id != null
																		? unitOptions.find((u) => u.value === row.unit_id)?.label ?? SAMPLE_CELL_LABELS[col.columnType]
																		: SAMPLE_CELL_LABELS[col.columnType]}
															</td>
														))}
													</tr>
												))
											) : (
												<tr>
													<td
														className={cn(
															cellBorder,
															"bg-gray-50 text-center font-roboto text-xs text-primary-500"
														)}
													>
														1
													</td>
													{columns.map((col) => (
														<td
															key={col.localId}
															className={cn(
																cellBorder,
																"px-2 py-2 font-roboto text-xs italic text-gray-400"
															)}
														>
															{SAMPLE_CELL_LABELS[col.columnType]}
														</td>
													))}
												</tr>
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							btntype="secondary"
							className="mr-3"
							data-testid="cancel-create-table"
							onClick={onClose}
						>
							Cancel
						</Button>
						<Button
							btntype="primary"
							data-testid="submit-create-table"
							disabled={isSubmitting}
							onClick={handleSubmit}
						>
							{isSubmitting
								? "Saving..."
								: isEditMode
									? "Save"
									: "Add Table"}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Preset change warning */}
			<AlertModal
				iconType="warning"
				isOpen={showPresetWarning}
				modalType="alert"
				onClose={() => {
					setShowPresetWarning(false);
					setPendingPreset("");
				}}
				primaryButton={{
					onClick: () => {
						setShowPresetWarning(false);
						applyPreset(pendingPreset);
						setPendingPreset("");
					},
					text: "Replace",
				}}
				secondaryButton={{
					onClick: () => {
						setShowPresetWarning(false);
						setPendingPreset("");
					},
					text: "Cancel",
				}}
				title="Replace Column Structure?"
			>
				<p className="text-center text-sm text-gray-600">
					Changing the preset will replace your current column structure. Are
					you sure?
				</p>
			</AlertModal>

			{/* Total column removal warning */}
			<AlertModal
				iconType="warning"
				isOpen={showTotalRemovalWarning}
				modalType="alert"
				onClose={() => {
					setShowTotalRemovalWarning(false);
					setPendingColumnDelete(null);
				}}
				primaryButton={{
					onClick: () => {
						setShowTotalRemovalWarning(false);
						if (pendingColumnDelete) {
							executeColumnDelete(pendingColumnDelete);
						}
						setPendingColumnDelete(null);
					},
					text: "Continue",
				}}
				secondaryButton={{
					onClick: () => {
						setShowTotalRemovalWarning(false);
						setPendingColumnDelete(null);
					},
					text: "Cancel",
				}}
				title="Remove Column?"
			>
				<p className="text-center text-sm text-gray-600">
					Removing this column will also remove the Total column. Continue?
				</p>
			</AlertModal>
		</>
	);
};

export { CreateQuoteTableModal };
